import React, { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/customSupabaseClient';
import { format } from 'date-fns';
import { emailService } from '@/lib/emailService';

import BookingHeader from './BookingHeader';
import BookingForm from './BookingForm';
import PriceEstimate from './PriceEstimate';
import { usePriceCalculator } from './usePriceCalculator';
import { useLocation } from 'react-router-dom';
import ErrorBoundary from '@/components/ErrorBoundary';
import useGeocodePrefill from '@/hooks/useGeocodePrefill';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const BookingNew = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();

  const [formState, setFormState] = useState({
    tripType: 'One Way',
    passengers: 1,
    vehicle: 'Luxury SUV',
    date: new Date(),
    time: '12:00',
    hours: 3,
    name: '',
    email: '',
    phone: '',
    pickup: '',
    dropoff: '',
    instructions: '',
    selectedPickup: null,
    selectedDropoff: null,
  });

  const { price, details, distance } = usePriceCalculator(formState);

  const handleFormChange = useCallback((field, value) => {
    setFormState(prevState => ({ ...prevState, [field]: value }));
  }, []);

  useEffect(() => {
    // Prefer navigation state to preserve exact strings
    const state = location.state;
    const statePickup = state && state.pickup;
    const stateDropoff = state && state.dropoff;
    const statePassengers = state && state.passengers;
    const stateDate = state && state.date;
    const stateTime = state && state.time;
    const stateVehicle = state && state.vehicle;
    const stateTripType = state && state.tripType;

    // Fallback to query params if state not provided
    const params = new URLSearchParams(location.search);
    const qpPickup = params.get('pickup');
    const qpDropoff = params.get('dropoff');

    const pickup = statePickup ?? qpPickup;
    const dropoff = stateDropoff ?? qpDropoff;

    if (pickup || dropoff || statePassengers || stateDate || stateTime || stateVehicle || stateTripType) {
      setFormState(prev => ({
        ...prev,
        pickup: pickup || prev.pickup,
        dropoff: dropoff || prev.dropoff,
        passengers: statePassengers || prev.passengers,
        date: stateDate ? new Date(stateDate) : prev.date,
        time: stateTime || prev.time,
        vehicle: stateVehicle || prev.vehicle,
        tripType: stateTripType || prev.tripType,
      }));
    }
  }, [location.state, location.search]);

  // Use reusable geocode prefill hook only if values exist (avoid conflicting with typing/suggestions)
  const shouldPrefill = Boolean(formState.pickup || formState.dropoff);
  useGeocodePrefill(formState, setFormState, shouldPrefill);

  const resetForm = useCallback(() => {
    setFormState({
      tripType: 'One Way',
      passengers: 1,
      vehicle: 'Luxury SUV',
      date: new Date(),
      time: '12:00',
      hours: 3,
      name: '',
      email: '',
      phone: '',
      pickup: '',
      dropoff: '',
      instructions: '',
      selectedPickup: null,
      selectedDropoff: null,
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const bookingData = {
        trip_type: formState.tripType.toLowerCase().replace(' ', '_'), // Convert to one_way, round_trip, hourly
        vehicle_type: formState.vehicle,
        pickup_time: `${format(formState.date, 'yyyy-MM-dd')} ${formState.time}`,
        hours: formState.hours,
        customer_name: formState.name,
        customer_email: formState.email,
        customer_phone: formState.phone,
        pickup_address: formState.pickup,
        dropoff_address: formState.dropoff,
        special_requests: formState.instructions,
        price: parseFloat(price) || 0,
        distance_miles: distance || 0,
        status: 'pending',
        // Add coordinates if available
        pickup_lat: formState.selectedPickup?.lat || null,
        pickup_lng: formState.selectedPickup?.lng || null,
        dropoff_lat: formState.selectedDropoff?.lat || null,
        dropoff_lng: formState.selectedDropoff?.lng || null,
      };

      const { data, error } = await supabase
        .from('bookings')
        .insert([bookingData])
        .select();

      if (error) {
        console.error('Booking error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          bookingData: bookingData
        });
        toast({
          title: "Booking Failed",
          description: `Error: ${error.message || 'Unknown error occurred'}`,
          variant: "destructive",
        });
        return;
      }

      // Booking submitted successfully
      
      // Send email notifications
      if (data && data[0]) {
        const bookingId = data[0].id;
        const bookingWithId = { ...bookingData, id: bookingId };
        
        console.log('📧 Starting email sending process...');
        
        // Test the Edge Function directly first
        console.log('🧪 Testing Edge Function directly...');
        try {
          const testData = {
            record: {
              email: formState.email,
              name: bookingData.customer_name,
              phone: bookingData.customer_phone,
              trip_type: bookingData.trip_type,
              datetime: bookingData.pickup_time,
              vehicle: bookingData.vehicle_type,
              passengers: formState.passengers || 1,
              pickup: bookingData.pickup_address,
              dropoff: bookingData.dropoff_address,
              hours: bookingData.hours,
              estimate_amount: bookingData.price,
              estimate_distance: bookingData.distance_miles,
              instructions: bookingData.special_requests || 'None'
            }
          };
          
          console.log('🧪 Test data being sent:', testData);
          
          const { data: testResponse, error: testError } = await supabase.functions.invoke('send-booking-email', {
            body: testData
          });
          
          if (testError) {
            console.error('🧪 Test function error:', testError);
            console.error('🧪 Test error details:', JSON.stringify(testError, null, 2));
          } else {
            console.log('🧪 Test function success:', testResponse);
          }
        } catch (testException) {
          console.error('🧪 Test exception:', testException);
        }
        
        // Send both customer confirmation and admin notification
        const emailResults = await emailService.sendBookingEmails(bookingWithId, formState.email, formState.passengers);
        
        console.log('📧 Email results:', emailResults);
        
        if (emailResults.customerEmailSent && emailResults.adminEmailSent) {
          toast({
            title: "Booking Submitted Successfully!",
            description: "✅ Confirmation email sent to your email address. Admin has been notified.",
          });
        } else if (emailResults.customerEmailSent) {
          toast({
            title: "Booking Submitted!",
            description: "✅ Confirmation email sent to your email address. ⚠️ Admin notification failed.",
          });
        } else if (emailResults.adminEmailSent) {
          toast({
            title: "Booking Submitted!",
            description: "⚠️ Confirmation email failed to send. Admin has been notified. We'll contact you soon.",
          });
        } else {
          toast({
            title: "Booking Submitted!",
            description: "⚠️ Email notifications failed to send. We'll contact you soon to confirm details.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Booking Submitted!",
          description: "Thank you for your booking request. We'll contact you soon to confirm details.",
        });
      }

      resetForm();
    } catch (error) {
      // Unexpected error
      toast({
        title: "Booking Failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Book Your Ride - Akib Black Car</title>
        <meta name="description" content="Book your luxury transportation with Akib Black Car. Professional chauffeur services for all occasions." />
      </Helmet>
      
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="min-h-screen bg-brand-dark"
      >
        <BookingHeader />
        
        <div className="container mx-auto px-4 py-8">
          <ErrorBoundary>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <BookingForm 
                formState={formState} 
                onFormChange={handleFormChange} 
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
              
              <div className="lg:col-span-1">
                <PriceEstimate 
                  price={price} 
                  details={details} 
                  distance={distance}
                  tripType={formState.tripType}
                  vehicle={formState.vehicle}
                  hours={formState.hours}
                />
              </div>
            </div>
          </ErrorBoundary>
        </div>
      </motion.div>
    </>
  );
};

export default BookingNew;
